import "@/App.css";
import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import CookieBanner from "./components/CookieBanner";
import { installWhatsAppClickTracker, installAffiliateClickTracker } from "./lib/analytics";

// Code-splitting: each page is its own bundle, only loaded on demand
const Landing = lazy(() => import("./pages/Landing"));
const Hoteles = lazy(() => import("./pages/Hoteles"));
const Guia = lazy(() => import("./pages/Guia"));
const Faq = lazy(() => import("./pages/Faq"));
const Contacto = lazy(() => import("./pages/Contacto"));
const Restaurantes = lazy(() => import("./pages/Restaurantes"));
const Atracciones = lazy(() => import("./pages/Atracciones"));
const Trucos = lazy(() => import("./pages/Trucos"));
const Espectaculos = lazy(() => import("./pages/Espectaculos"));
const Frozen = lazy(() => import("./pages/Frozen"));
const Avengers = lazy(() => import("./pages/Avengers"));
const Personajes = lazy(() => import("./pages/Personajes"));
const DisneyVillage = lazy(() => import("./pages/DisneyVillage"));
const Paris = lazy(() => import("./pages/Paris"));
const Orlando = lazy(() => import("./pages/Orlando"));
const OrlandoParkDetail = lazy(() => import("./pages/OrlandoParkDetail"));
const DisneylandPark = lazy(() => import("./pages/parks/DisneylandPark"));
const AdventureWorld = lazy(() => import("./pages/parks/AdventureWorld"));
const Itinerarios = lazy(() => import("./pages/Itinerarios"));
const PagoConfirmado = lazy(() => import("./pages/PagoConfirmado"));
const Reservar = lazy(() => import("./pages/Reservar"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AvisoLegal = lazy(() => import("./pages/AvisoLegal"));
const Privacidad = lazy(() => import("./pages/Privacidad"));
const Cookies = lazy(() => import("./pages/Cookies"));
const Terminos = lazy(() => import("./pages/Terminos"));

// Admin (proposals tool)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminShell = lazy(() => import("./pages/admin/AdminShell"));
const AdminPropuestas = lazy(() => import("./pages/admin/AdminPropuestas"));
const AdminPlantillas = lazy(() => import("./pages/admin/AdminPlantillas"));
const AdminAfiliados = lazy(() => import("./pages/admin/AdminAfiliados"));
const AdminItinerarios = lazy(() => import("./pages/admin/AdminItinerarios"));
const AdminItinerarioEditor = lazy(() => import("./pages/admin/AdminItinerarioEditor"));
const AdminCatalogoItinerario = lazy(() => import("./pages/admin/AdminCatalogoItinerario"));
const ItinerarioPublico = lazy(() => import("./pages/ItinerarioPublico"));
const TrucosYMaleta = lazy(() => import("./pages/TrucosYMaleta"));
const TrucosYMaletaOrlando = lazy(() => import("./pages/TrucosYMaletaOrlando"));
const AssetProcesoItinerario = lazy(() => import("./pages/AssetProcesoItinerario"));
const AssetProcesoCarousel = lazy(() => import("./pages/AssetProcesoCarousel"));
const AssetParquesOrlando = lazy(() => import("./pages/AssetParquesOrlando"));
const AssetWildernessLodge = lazy(() => import("./pages/AssetWildernessLodge"));
const AssetPlanesComida2027 = lazy(() => import("./pages/AssetPlanesComida2027"));
const AtraccionesAltura = lazy(() => import("./pages/AtraccionesAltura"));
const AdminAlturaDoc = lazy(() => import("./pages/admin/AdminAlturaDoc"));
const AdminPlanBoo = lazy(() => import("./pages/admin/AdminPlanBoo"));
const AdminPlanPricing = lazy(() => import("./pages/admin/AdminPlanPricing"));
const PreferenciasItinerario = lazy(() => import("./pages/PreferenciasItinerario"));
const AdminPrefs = lazy(() => import("./pages/admin/AdminPrefs"));
const ItinerariosMockup = lazy(() => import("./pages/admin/ItinerariosMockup"));
const AdminEditor = lazy(() => import("./pages/admin/AdminEditor"));
const PropuestaPublica = lazy(() => import("./pages/PropuestaPublica"));

// Minimal route-level loader (keeps brand colours, no layout shift)
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-[#FAD0C9]/10 to-[#B3C5DF]/10">
      <div className="flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-[#D99A90] border-t-transparent animate-spin" />
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-500">Encantando…</span>
      </div>
    </div>
  );
}

function App() {
  useEffect(() => {
    installWhatsAppClickTracker();
    installAffiliateClickTracker();
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/chat" element={<Navigate to="/reservar" replace />} />
            <Route path="/hoteles" element={<Hoteles />} />
            <Route path="/maleta" element={<Guia />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contacto" element={<Contacto />} />
            <Route path="/restaurantes" element={<Restaurantes />} />
            <Route path="/atracciones" element={<Atracciones />} />
            <Route path="/trucos" element={<Trucos />} />
            <Route path="/espectaculos" element={<Espectaculos />} />
            <Route path="/frozen" element={<Frozen />} />
            <Route path="/avengers" element={<Avengers />} />
            <Route path="/personajes" element={<Personajes />} />
            <Route path="/disney-village" element={<DisneyVillage />} />
            <Route path="/paris" element={<Paris />} />
            <Route path="/orlando" element={<Orlando />} />
            <Route path="/orlando/:parkSlug" element={<OrlandoParkDetail />} />

            {/* Disneyland Park sub-routes */}
            <Route path="/disneyland-park" element={<DisneylandPark />} />
            <Route path="/disneyland-park/atracciones" element={<Atracciones parkFilter="disneyland" />} />
            <Route path="/disneyland-park/restaurantes" element={<Restaurantes parkFilter="disneyland" />} />
            <Route path="/disneyland-park/espectaculos" element={<Espectaculos parkFilter="disneyland" />} />
            <Route path="/disneyland-park/personajes" element={<Personajes parkFilter="disneyland" />} />

            {/* Adventure World sub-routes */}
            <Route path="/adventure-world" element={<AdventureWorld />} />
            <Route path="/adventure-world/atracciones" element={<Atracciones parkFilter="adventure" />} />
            <Route path="/adventure-world/restaurantes" element={<Restaurantes parkFilter="adventure" />} />
            <Route path="/adventure-world/espectaculos" element={<Espectaculos parkFilter="adventure" />} />
            <Route path="/adventure-world/personajes" element={<Personajes parkFilter="adventure" />} />

            <Route path="/itinerarios" element={<Itinerarios />} />
            <Route path="/itinerarios/pago-confirmado" element={<PagoConfirmado />} />
            <Route path="/reservar" element={<Reservar />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/aviso-legal" element={<AvisoLegal />} />
            <Route path="/privacidad" element={<Privacidad />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/terminos" element={<Terminos />} />

            {/* Admin (proposals) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminPropuestas />} />
              <Route path="propuestas" element={<AdminPropuestas />} />
              <Route path="propuestas/:id" element={<AdminEditor mode="proposal" />} />
              <Route path="plantillas" element={<AdminPlantillas />} />
              <Route path="plantillas/:id" element={<AdminEditor mode="template" />} />
              <Route path="afiliados" element={<AdminAfiliados />} />
              <Route path="itinerarios" element={<AdminItinerarios />} />
              <Route path="itinerarios/catalogo" element={<AdminCatalogoItinerario />} />
              <Route path="itinerarios/:id" element={<AdminItinerarioEditor />} />
              <Route path="itinerarios-preview" element={<ItinerariosMockup />} />
              <Route path="atracciones-altura" element={<AdminAlturaDoc />} />
              <Route path="plan-boo" element={<AdminPlanBoo />} />
              <Route path="precios" element={<AdminPlanPricing />} />
              <Route path="itinerarios/:itineraryId/preferencias" element={<AdminPrefs />} />
            </Route>

            {/* Public proposal view (shareable URL) */}
            <Route path="/propuesta/:slug" element={<PropuestaPublica />} />
            <Route path="/itinerario/:slug/preferencias" element={<PreferenciasItinerario />} />
            <Route path="/itinerario/:slug" element={<ItinerarioPublico />} />
            <Route path="/trucos-y-maleta" element={<TrucosYMaleta />} />
            <Route path="/trucos-y-maleta-orlando" element={<TrucosYMaletaOrlando />} />
            <Route path="/asset/proceso-itinerario" element={<AssetProcesoItinerario />} />
            <Route path="/asset/proceso-carousel" element={<AssetProcesoCarousel />} />
            <Route path="/asset/parques-orlando" element={<AssetParquesOrlando />} />
            <Route path="/asset/wilderness-lodge-actividades" element={<AssetWildernessLodge />} />
            <Route path="/asset/planes-comida-2027" element={<AssetPlanesComida2027 />} />
            <Route path="/atracciones-sin-altura" element={<AtraccionesAltura />} />
          </Routes>
        </Suspense>
        <CookieBanner />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
